import { Component } from '@theme/component';
import { SlideshowSelectEvent, ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 * @property {HTMLElement} [galleryProgress] - The mWeb pagination progress bar.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    // slideshow:select bubbles up from the carousel; drive the progress bar fill from its index.
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect, { signal });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);

    // On mWeb the gallery sits above the variant picker, so a per-variant swap happens off-screen.
    // Bring the freshly-swapped gallery into view so the change is visible. PDP-only by construction
    // (<media-gallery> renders only on the product page); focus stays on the tapped swatch.
    this.#scrollSwappedGalleryIntoView(newMediaGallery);
  };

  /**
   * Mobile-only: smooth-scroll the swapped gallery to the top of the viewport. Desktop is left
   * alone (the gallery is already in view). Honours prefers-reduced-motion. Does not move focus.
   *
   * @param {Element} gallery - The newly-inserted media gallery.
   */
  #scrollSwappedGalleryIntoView(gallery) {
    if (!window.matchMedia('(max-width: 749px)').matches) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // rAF so the new gallery is laid out before we measure/scroll to it.
    requestAnimationFrame(() => {
      gallery.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    });
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Advances the mWeb progress bar to the selected slide. The CSS maps (--index + 1) / --total to
   * the fill width, so we only set the index here.
   * @param {SlideshowSelectEvent} event - The slideshow:select event.
   */
  #handleSlideshowSelect = (event) => {
    this.refs.galleryProgress?.style.setProperty('--index', String(event.detail.index));
  };

  get slideshow() {
    return this.refs.slideshow;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
